<!----------------- BEGIN TS ---------------------->
<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import panzoom, { PanZoom, PanZoomOptions, TransformOrigin } from "panzoom";
import TheVideoPlayer from "@/components/TheVideoPlayer.vue";

@Component({
  components: {
    TheVideoPlayer,
  },
})
export default class ThePanZoomOverlay extends Vue {
  // ---------- Props ----------

  /** The video player element to do panzoom on */
  @Prop() videoPlayer!: HTMLElement;

  /** Show the panzoom tools when hovering over viewer. */
  @Prop() hovering!: boolean;

  // ------- Local Vars --------
  /** The panzoom object. */
  thePanZoomObject!: PanZoom;

  /** The current zoomLevel as a float. */
  zoomLevel!: number;

  /** Defined minimum and maximum zoom levels. */
  // MAX_ZOOM = 4;
  MIN_ZOOM = 1;

  /** The config for the panZoom */
  panzoomConfig: PanZoomOptions = {
    bounds: true,
    boundsPadding: 1,
    minZoom: this.MIN_ZOOM,
    // maxZoom: this.MAX_ZOOM,
    onDoubleClick: function (e) {
      return true; // tells the library to not preventDefault, and not stop propagation
    },
  };

  /** Whether or not to show the +/- magnifying glass buttons */
  showZoomTools = false;

  /** This tells panzoom where the "middle" of the panzoom is */
  tranformOrigin!: TransformOrigin;

  // --------- Watchers --------

  @Watch("hovering")
  userHovering(status: boolean): void {
    if (status && this.thePanZoomObject) {
      this.thePanZoomObject.resume();
      this.showZoomTools = true;
    } else if (this.thePanZoomObject) {
      this.thePanZoomObject.pause();
      this.showZoomTools = false;
    } else {
      // Since this is called on every hover, it's likely okay
      // to not show this warning because it can be misleading
      // console.warn("Panzoom is currently unavailable.");
    }
  }

  @Watch("videoPlayer")
  videoPlayerChange(): void {
    // only set up the video with panzoom once the player exists
    if (this.videoPlayer) {
      this.setupVideo(this.videoPlayer);
    }
  }

  // ------- Lifecycle ---------

  // --------- Methods ---------

  /** Set up the video component with the panZoom config */
  setupVideo(videoElem: HTMLElement): void {
    this.zoomLevel = 1;
    this.tranformOrigin = {
      x: 0.5,
      y: 0.5,
    };
    this.thePanZoomObject = panzoom(videoElem, this.panzoomConfig);
    // Fix to make sure it stays in bounds but still allows zoom thanks to https://github.com/anvaka/panzoom/issues/33#issuecomment-498225590
    this.thePanZoomObject.on("panend", (e: PanZoom) => {
      const { x } = e.getTransform();
      const maxTranslate =
        videoElem.getBoundingClientRect().width - videoElem.clientWidth;

      if (Math.abs(x) >= maxTranslate) {
        e.moveBy(-(x + maxTranslate), 0, true);
      }
    });
  }

  /** Function to determine whether the user is hovering over the video. */
  userOver(status: boolean): void {
    if (status && this.thePanZoomObject) {
      this.thePanZoomObject.resume();
      this.showZoomTools = true;
    } else {
      this.thePanZoomObject.pause();
      this.showZoomTools = false;
    }
  }

  /** Handle zoom by a specified parameter. */
  zoom(value: number): void {
    this.zoomLevel = this.thePanZoomObject.getTransform().scale + value;
    this.thePanZoomObject.smoothZoomAbs(
      this.tranformOrigin.x,
      this.tranformOrigin.y,
      this.zoomLevel
    );
    // This is for if you want to set a Maximum zoom level
    // if (this.zoomLevel >= this.MAX_ZOOM) {
    //   this.thePanZoomObject.smoothZoomAbs(
    //     this.tranformOrigin.x,
    //     this.tranformOrigin.y,
    //     this.MAX_ZOOM
    //   );
    //   this.zoomLevel = this.MAX_ZOOM;
    // } else if (this.zoomLevel <= this.MIN_ZOOM) {
    //   this.thePanZoomObject.smoothZoomAbs(
    //     this.tranformOrigin.x,
    //     this.tranformOrigin.y,
    //     this.MIN_ZOOM
    //   );
    //   this.zoomLevel = this.MIN_ZOOM;
    // } else {
    //   this.thePanZoomObject.smoothZoomAbs(
    //     this.tranformOrigin.x,
    //     this.tranformOrigin.y,
    //     this.zoomLevel
    //   );
    // }
  }
}
</script>
<!----------------- END TS ------------------------>

<!----------------- BEGIN HTML -------------------->
<template lang="html">
  <div
    class="zoom-buttons"
    :class="{ hide: !showZoomTools }"
    @mouseover="$emit('hover', true)"
    @mouseleave="$emit('hover', false)"
  >
    <button type="button" class="btn btn-light zoom-button" @click="zoom(0.5)">
      <b-icon class="zoom-icon" icon="zoom-in"></b-icon>
    </button>
    <button type="button" class="btn btn-light zoom-button" @click="zoom(-0.5)">
      <b-icon class="zoom-icon" icon="zoom-out"></b-icon>
    </button>
  </div>
</template>
<!----------------- END HTML ---------------------->

<!----------------- BEGIN SCSS -------------------->
<style scoped lang="scss">
.zoom-buttons {
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: 32px;
  margin-bottom: -160px;
  margin-top: 20px;
  z-index: 1;
  pointer-events: auto;
  cursor: pointer;

  &.hide {
    opacity: 0;
    animation: fadeOut 1s;
  }

  .zoom-button {
    padding: 15px;
    border-radius: 50%;
    background-color: white;
    cursor: pointer;
    border: none;
    margin-bottom: 5px;
    opacity: 0.3;

    &:hover {
      background-color: white;
      opacity: 0.6;
    }

    .zoom-icon {
      width: 35px;
      height: 35px;
      color: black;

      @media only screen and (max-width: 1200px),
        screen and (max-height: 615px) {
        width: 25px;
        height: 25px;
      }
      @media only screen and (max-width: 400px),
        screen and (max-height: 400px) {
        width: 15px;
        height: 15px;
      }
    }
  }

  @media only screen and (max-width: 1200px), screen and (max-height: 615px) {
    margin-bottom: -128px;

    .zoom-button {
      padding: 12px;
    }
  }

  @media only screen and (max-width: 400px), screen and (max-height: 400px) {
    margin-bottom: -94px;
    margin-right: 26px;

    .zoom-button {
      padding: 8px;
    }
  }
}

@keyframes fadeOut {
  from {
    opacity: 0.6;
  }
  to {
    opacity: 0;
  }
}
</style>
<!----------------- END SCSS ---------------------->
